import React, { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { useFlashcards } from '../../context/FlashcardContext/index';
import { useTheme } from '../../context/ThemeContext';
import SwipeCard from '../SwipeCard';
import { playSound } from '../Audio';
import { FeedbackOverlay, ProgressBar, ScoreDisplay, Rewards } from '../GameElements';
import SettingsDialog from '../SettingsDialog';
import WelcomeScreen from '../WelcomeScreen';
import ChallengeSummary from '../ChallengeSummary';
import { getReviewStyles } from './styles';
import { getCardsForStudyMode, getNextBatchOfCards } from './utils';
import { getNextDifficultyGroupId } from '../groupSelection';
import {
  buildChallengeUrl,
  clearChallengeHash,
  createChallengePayload,
  createSeed,
  formatDuration,
  parseChallengeHash,
  seededShuffle,
  shareText
} from '../../utils/challenge';
import {
  getPushState,
  getReminderSettings,
  isPushSupported,
  saveReminderSettings,
  subscribeToPush,
  syncPushStudyActivity,
  unsubscribeFromPush,
} from '../../utils/pushNotifications';

const CreateCard = lazy(() => import('../CreateCard'));
const WELCOME_VERSION_KEY = 'welcomeShown:v2';

const createInitialSessionStats = () => ({
  correct: 0,
  wrong: 0,
  streak: 0,
  bestStreak: 0,
  startTime: Date.now(),
});

export default function ReviewScreen() {
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem(WELCOME_VERSION_KEY));
  const [sessionCards, setSessionCards] = useState([]);
  const [, setPreviousBatch] = useState([]);
  const [cardsReviewed, setCardsReviewed] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showManageCards, setShowManageCards] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastCorrect, setLastCorrect] = useState(null);
  const [isCardVisible, setCardVisible] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [challengeData, setChallengeData] = useState(() => parseChallengeHash());
  const [sessionStats, setSessionStats] = useState(createInitialSessionStats);
  const [challengeResult, setChallengeResult] = useState(null);
  const [timerNow, setTimerNow] = useState(Date.now());
  const [comboMessage, setComboMessage] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [pushMessage, setPushMessage] = useState('');
  const [pushBusy, setPushBusy] = useState(false);
  const [reminderSettings, setReminderSettings] = useState({
    reminderEnabled: false,
    reminderTime: '19:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  });
  const [pushState, setPushState] = useState({
    supported: false,
    permission: 'default',
    subscribed: false,
  });

  const handleCloseWelcome = () => {
    localStorage.setItem(WELCOME_VERSION_KEY, 'true');
    setShowWelcome(false);
  };

  const {
    allFlashcards,
    flashcards,
    score,
    studyStats,
    studyMode,
    updateScore,
    isMuted,
    toggleMute,
    groups,
    activeGroupId,
    setActiveGroupId,
    setStudyMode
  } = useFlashcards();

  const activeGroup = groups.find(group => group.id === activeGroupId);
  const isChallengeMode = Boolean(challengeData);
  const challengeCards = useMemo(() => {
    if (!challengeData) return [];
    return seededShuffle(challengeData.cards, challengeData.seed);
  }, [challengeData]);
  const modeCards = useMemo(() => getCardsForStudyMode(flashcards, studyMode), [flashcards, studyMode]);
  const activeCards = isChallengeMode ? challengeCards : (studyMode === 'random' ? sessionCards : modeCards);
  const totalCardsInGroup = activeCards.length;
  const sessionScore = sessionStats.correct - sessionStats.wrong;
  const elapsedMs = challengeResult?.durationMs ?? (timerNow - sessionStats.startTime);

  const { colors } = useTheme();
  const styles = getReviewStyles(colors, isChallengeMode ? { total: sessionScore } : score);

  const resetSession = (nextIndex = 0) => {
    setSessionStats(createInitialSessionStats());
    setChallengeResult(null);
    setComboMessage('');
    setCurrentIndex(nextIndex);
    setShowFeedback(false);
    setCardVisible(true);
    setIsTransitioning(false);
    setTimerNow(Date.now());
  };

  useEffect(() => {
    if (!isChallengeMode && !activeGroupId && groups.length > 0) {
      setActiveGroupId(groups[0].id);
    }
  }, [activeGroupId, groups, isChallengeMode, setActiveGroupId]);

  useEffect(() => {
    if (!isChallengeMode && activeGroupId) {
      const activeGroupCards = allFlashcards.filter(card => card.groupId === activeGroupId);
      const reviewedCount = activeGroupCards.filter(card => card.reviewed).length;
      setCardsReviewed(reviewedCount);

      if (activeGroupCards.length > 0 && reviewedCount === activeGroupCards.length) {
        const nextDifficultyGroupId = getNextDifficultyGroupId(groups, activeGroupId);
        if (nextDifficultyGroupId) {
          setActiveGroupId(nextDifficultyGroupId);
          setPreviousBatch([]);
          setCurrentIndex(0);
          setSessionCards([]);
          resetSession();
        }
      }
    }
  }, [activeGroupId, allFlashcards, groups, isChallengeMode, setActiveGroupId]);

  useEffect(() => {
    const syncChallengeFromHash = () => {
      setChallengeData(parseChallengeHash());
      setShowManageCards(false);
      resetSession();
      setSessionCards([]);
    };

    window.addEventListener('hashchange', syncChallengeFromHash);
    return () => window.removeEventListener('hashchange', syncChallengeFromHash);
  }, []);

  useEffect(() => {
    if (!isChallengeMode || challengeResult) return undefined;

    const interval = window.setInterval(() => {
      setTimerNow(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, [challengeResult, isChallengeMode]);

  useEffect(() => {
    if (!shareMessage) return undefined;
    const timeout = window.setTimeout(() => setShareMessage(''), 2400);
    return () => window.clearTimeout(timeout);
  }, [shareMessage]);

  useEffect(() => {
    if (!pushMessage) return undefined;
    const timeout = window.setTimeout(() => setPushMessage(''), 2800);
    return () => window.clearTimeout(timeout);
  }, [pushMessage]);

  useEffect(() => {
    if (!isPushSupported()) {
      return undefined;
    }

    let cancelled = false;

    const syncPushState = async () => {
      try {
        const nextState = await getPushState();
        if (!cancelled) {
          setPushState(nextState);
          if (nextState.subscribed) {
            const nextSettings = await getReminderSettings();
            if (!cancelled) {
              setReminderSettings(nextSettings);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          setPushState({
            supported: true,
            permission: Notification.permission,
            subscribed: false,
          });
        }
      }
    };

    syncPushState();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!pushState.subscribed || !studyStats.lastStudyDate) {
      return undefined;
    }

    syncPushStudyActivity(studyStats.lastStudyDate).catch(() => {});
    return undefined;
  }, [pushState.subscribed, studyStats.lastStudyDate, studyStats.totalAnswers]);

  useEffect(() => {
    if (isChallengeMode) return undefined;

    if (studyMode !== 'random') {
      return undefined;
    }

    if (flashcards.length > 0) {
      if (sessionCards.length === 0 || activeGroupId !== sessionCards[0]?.groupId) {
        const nextBatch = getNextBatchOfCards(flashcards, [], 10, studyMode);
        if (nextBatch.length > 0) {
          setSessionCards(nextBatch);
          setPreviousBatch([]);
          setCurrentIndex(0);
        }
      } else if (currentIndex >= sessionCards.length) {
        const nextBatch = getNextBatchOfCards(flashcards, sessionCards, 10, studyMode);
        if (nextBatch.length > 0) {
          setPreviousBatch(sessionCards);
          setSessionCards(nextBatch);
          setCurrentIndex(0);
        }
      }
    }

    return undefined;
  }, [currentIndex, sessionCards, flashcards, activeGroupId, isChallengeMode, studyMode]);

  const currentCard = activeCards[currentIndex];

  const handleChallengeShare = async (payload) => {
    try {
      const url = buildChallengeUrl(payload);
      const message = await shareText({
        title: payload.title,
        text: `Can you beat my StudySwipe challenge in ${payload.topic} ${payload.difficulty}?`,
        url
      });
      setShareMessage(message);
      setShowSettings(false);
    } catch (error) {
      console.error('Failed to share challenge:', error);
      setShareMessage('Sharing failed. Try again.');
    }
  };

  const refreshPushState = async () => {
    const nextState = await getPushState();
    setPushState(nextState);
    if (nextState.subscribed) {
      const nextSettings = await getReminderSettings();
      setReminderSettings(nextSettings);
    } else {
      setReminderSettings(prev => ({
        ...prev,
        reminderEnabled: false,
      }));
    }
  };

  const handleTogglePushNotifications = async () => {
    if (!pushState.supported) {
      setPushMessage('Daily reminders are not supported on this device.');
      return;
    }

    setPushBusy(true);
    try {
      if (pushState.subscribed) {
        await unsubscribeFromPush();
        setPushMessage('Daily reminder permission turned off.');
      } else {
        await subscribeToPush();
        setPushMessage('Daily reminder permission turned on.');
      }
      await refreshPushState();
    } catch (error) {
      setPushMessage(error.message || 'Notification setup failed.');
    } finally {
      setPushBusy(false);
    }
  };

  const handleReminderToggle = async () => {
    if (!pushState.subscribed) {
      setPushMessage('Enable notifications first.');
      return;
    }

    setPushBusy(true);
    try {
      const nextSettings = await saveReminderSettings({
        reminderEnabled: !reminderSettings.reminderEnabled,
        reminderTime: reminderSettings.reminderTime,
        timezone: reminderSettings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      });
      setReminderSettings(nextSettings);
      setPushMessage(nextSettings.reminderEnabled ? 'Daily reminder on.' : 'Daily reminder off.');
    } catch (error) {
      setPushMessage(error.message || 'Reminder update failed.');
    } finally {
      setPushBusy(false);
    }
  };

  const handleReminderTimeChange = async (reminderTime) => {
    setReminderSettings(prev => ({
      ...prev,
      reminderTime,
    }));

    if (!pushState.subscribed || !reminderSettings.reminderEnabled) {
      return;
    }

    setPushBusy(true);
    try {
      const nextSettings = await saveReminderSettings({
        reminderEnabled: true,
        reminderTime,
        timezone: reminderSettings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      });
      setReminderSettings(nextSettings);
      setPushMessage(`Daily reminder set for ${reminderTime}.`);
    } catch (error) {
      setPushMessage(error.message || 'Reminder time update failed.');
    } finally {
      setPushBusy(false);
    }
  };

  const shareCurrentChallenge = async () => {
    if (!activeGroup || flashcards.length === 0) {
      setShareMessage('Add cards to this topic before sharing a challenge.');
      return;
    }

    const payload = createChallengePayload({
      title: `${activeGroup.topic} ${activeGroup.difficulty} Challenge`,
      topic: activeGroup.topic,
      difficulty: activeGroup.difficulty,
      seed: createSeed(),
      cards: flashcards
    });

    await handleChallengeShare(payload);
  };

  const shareChallengeResult = async () => {
    if (!challengeData || !challengeResult) return;

    const payload = createChallengePayload({
      title: challengeData.title,
      topic: challengeData.topic,
      difficulty: challengeData.difficulty,
      seed: challengeData.seed,
      cards: challengeData.cards,
      sourceResult: {
        score: challengeResult.score,
        accuracy: challengeResult.accuracy,
        bestStreak: challengeResult.bestStreak,
        durationMs: challengeResult.durationMs,
      }
    });

    await handleChallengeShare(payload);
  };

  const exitChallengeMode = () => {
    clearChallengeHash();
    setChallengeData(null);
    setChallengeResult(null);
    setComboMessage('');
    setShowSettings(false);
    resetSession();
  };

  const handleSwipe = async (isCorrect) => {
    if (!isMuted) {
      playSound(isCorrect ? 'correct' : 'incorrect');
    }

    const nextStats = {
      correct: sessionStats.correct + (isCorrect ? 1 : 0),
      wrong: sessionStats.wrong + (isCorrect ? 0 : 1),
      streak: isCorrect ? sessionStats.streak + 1 : 0,
      bestStreak: Math.max(sessionStats.bestStreak, isCorrect ? sessionStats.streak + 1 : 0),
      startTime: sessionStats.startTime,
    };

    setSessionStats(nextStats);

    if (isCorrect && [3, 5, 10].includes(nextStats.streak)) {
      setComboMessage(`${nextStats.streak} streak`);
    } else {
      setComboMessage('');
    }

    setLastCorrect(isCorrect);
    setShowFeedback(true);

    if (!isChallengeMode) {
      updateScore(isCorrect, currentCard.id);
    }

    setIsTransitioning(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    setShowFeedback(false);
    setCardVisible(false);
    await new Promise(resolve => setTimeout(resolve, 300));

    if (isChallengeMode) {
      if (currentIndex >= activeCards.length - 1) {
        const totalAnswered = nextStats.correct + nextStats.wrong;
        setChallengeResult({
          correct: nextStats.correct,
          wrong: nextStats.wrong,
          bestStreak: nextStats.bestStreak,
          totalCards: activeCards.length,
          accuracy: totalAnswered ? Math.round((nextStats.correct / totalAnswered) * 100) : 0,
          durationMs: Date.now() - sessionStats.startTime,
          score: nextStats.correct - nextStats.wrong,
        });
        setCurrentIndex(activeCards.length);
      } else {
        setCurrentIndex(prev => prev + 1);
      }
    } else if (studyMode === 'random') {
      if (currentIndex < sessionCards.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        setPreviousBatch(sessionCards);
        const nextBatch = getNextBatchOfCards(flashcards, sessionCards, 10, studyMode);
        setSessionCards(nextBatch);
        setCurrentIndex(0);
      }
    } else {
      setCurrentIndex(prev => {
        if (prev >= activeCards.length - 1) {
          return 0;
        }
        return prev + 1;
      });
    }

    await new Promise(resolve => setTimeout(resolve, 10));
    setCardVisible(true);
    setIsTransitioning(false);
  };

  const handleGroupChange = (newGroupId) => {
    setActiveGroupId(newGroupId);
    setPreviousBatch([]);
    setCardsReviewed(0);
    setCurrentIndex(0);
    setSessionCards([]);
    resetSession();
  };

  if (isChallengeMode && challengeResult) {
    return (
      <ChallengeSummary
        challenge={challengeData}
        result={challengeResult}
        onReplay={() => resetSession(0)}
        onExit={exitChallengeMode}
        onShareChallenge={() => handleChallengeShare(challengeData)}
        onShareResult={shareChallengeResult}
        shareMessage={shareMessage}
      />
    );
  }

  const mainContent = !activeCards.length ? (
    <div style={styles.container}>
      <div style={styles.emptyState}>
        <h2 style={styles.emptyTitle}>
          {isChallengeMode ? 'Challenge unavailable' : 'No cards in this group yet'}
        </h2>
        <p style={styles.emptyText}>
          {isChallengeMode
            ? 'This challenge link has no usable cards. Try another challenge or create a fresh one.'
            : 'Open card management to add cards, generate a set with AI, or switch to another topic.'}
        </p>
        <button
          onClick={() => (isChallengeMode ? exitChallengeMode() : setShowManageCards(true))}
          style={styles.emptyButton}
        >
          {isChallengeMode ? 'Exit Challenge' : 'Manage Cards'}
        </button>
      </div>
    </div>
  ) : !currentCard ? (
    <div style={styles.container}>
      <div style={styles.emptyState}>
        <h2 style={styles.emptyTitle}>Loading your next card</h2>
        <p style={styles.emptyText}>
          {isChallengeMode ? 'Preparing the challenge session.' : 'Preparing the next study batch for this group.'}
        </p>
      </div>
    </div>
  ) : (
    <div style={styles.container}>
      <FeedbackOverlay
        isCorrect={lastCorrect}
        show={showFeedback}
        message={comboMessage}
        emphasis={Boolean(comboMessage && lastCorrect)}
      />

      <>
        {isChallengeMode && (
          <div style={styles.challengeBanner}>
            <p style={styles.challengeTitle}>{challengeData.title}</p>
            <p style={styles.challengeMeta}>
              {challengeData.topic} • {challengeData.difficulty} • {formatDuration(elapsedMs)}
            </p>
          </div>
        )}

        <div style={styles.scoreContainer}>
          <ScoreDisplay score={isChallengeMode ? sessionScore : score.total} className={styles.score} />
          <div style={styles.scoreDetailsContainer}>
            <div style={styles.scoreDetail}>
              {(isChallengeMode ? sessionStats.correct : score.right)}<span style={styles.checkMark}>✓</span>
              <span style={styles.separator}>/</span>
              {(isChallengeMode ? sessionStats.wrong : score.wrong)}<span style={styles.wrongMark}>✗</span>
            </div>
            <div style={styles.progressDetail}>
              {Math.min(isChallengeMode ? currentIndex : cardsReviewed, totalCardsInGroup)}/{totalCardsInGroup}
            </div>
            {isChallengeMode && <div style={styles.timerDetail}>{formatDuration(elapsedMs)}</div>}
          </div>
          <Rewards score={isChallengeMode ? sessionScore : score.total} />
        </div>
        <ProgressBar current={isChallengeMode ? currentIndex : cardsReviewed} total={totalCardsInGroup} />
      </>

      {!isTransitioning && (
        <SwipeCard
          word={currentCard.question}
          answer={currentCard.answer}
          incorrect={currentCard.incorrect}
          onSwipe={handleSwipe}
          isVisible={isCardVisible}
        />
      )}
    </div>
  );

  return (
    <>
      {shareMessage && !showSettings && <div style={styles.toast}>{shareMessage}</div>}
      {showManageCards ? (
        <Suspense fallback={<div style={styles.container}>Loading...</div>}>
          <CreateCard onClose={() => setShowManageCards(false)} />
        </Suspense>
      ) : (
        <>
          {showWelcome && !isChallengeMode && <WelcomeScreen onClose={handleCloseWelcome} />}
          {mainContent}
        </>
      )}
      <button
        onClick={() => setShowSettings(true)}
        style={styles.controlButton}
        title="Menu"
      >
        ⚙️
      </button>

      <SettingsDialog
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        activeGroup={activeGroupId}
        groups={groups}
        allFlashcards={allFlashcards}
        onGroupChange={handleGroupChange}
        onManageCards={() => {
          setShowSettings(false);
          setShowManageCards(true);
        }}
        isMuted={isMuted}
        onToggleMute={toggleMute}
        onChallengeClick={shareCurrentChallenge}
        challengeEnabled={flashcards.length > 0 && Boolean(activeGroup)}
        shareMessage={shareMessage}
        pushSupported={pushState.supported}
        pushEnabled={pushState.subscribed}
        pushBusy={pushBusy}
        onTogglePushNotifications={handleTogglePushNotifications}
        reminderSettings={reminderSettings}
        onReminderToggle={handleReminderToggle}
        onReminderTimeChange={handleReminderTimeChange}
        pushStatusMessage={pushMessage}
        isChallengeMode={isChallengeMode}
        onExitChallenge={exitChallengeMode}
        studyStats={studyStats}
        studyMode={studyMode}
        onStudyModeChange={(mode) => {
          setStudyMode(mode);
          setCurrentIndex(0);
          setSessionCards([]);
          setShowSettings(false);
          setComboMessage('');
          setShareMessage('');
          setCardsReviewed(0);
        }}
        onInstallClick={() => {
          setShowSettings(false);
          const installEvent = new CustomEvent('showInstallPrompt');
          window.dispatchEvent(installEvent);
        }}
      />
    </>
  );
}
